import {
    ComputeBudgetProgram,
    ConfirmOptions,
    PublicKey,
    Signer,
    TransactionSignature,
} from '@solana/web3.js';
import {
    Rpc,
    buildAndSignTx,
    sendAndConfirmTx,
    DerivationMode,
    bn,
    LIGHT_TOKEN_PROGRAM_ID,
    assertV2Enabled,
} from '@lightprotocol/stateless.js';
import {
    createUpdateMintAuthorityInstruction,
    createUpdateFreezeAuthorityInstruction,
} from '../instructions/update-mint';
import { getMintInterface } from '../get-mint-interface';
import { ERR_MINT_MISSING_MERKLE_CONTEXT } from '../errors';

/**
 * Update the mint authority of a light-token mint.
 * Works for both compressed and decompressed light mints.
 *
 * @param rpc                    RPC connection
 * @param payer                  Fee payer (signer)
 * @param mint                   Mint address
 * @param currentMintAuthority   Current mint authority (signer)
 * @param newMintAuthority       New mint authority (or null to revoke)
 * @param confirmOptions         Optional confirm options
 */
export async function updateMintAuthority(
    rpc: Rpc,
    payer: Signer,
    mint: PublicKey,
    currentMintAuthority: Signer,
    newMintAuthority: PublicKey | null,
    confirmOptions?: ConfirmOptions,
): Promise<TransactionSignature> {
    assertV2Enabled();

    const mintInterface = await getMintInterface(
        rpc,
        mint,
        confirmOptions?.commitment,
        LIGHT_TOKEN_PROGRAM_ID,
    );

    if (!mintInterface.merkleContext) {
        throw new Error(ERR_MINT_MISSING_MERKLE_CONTEXT);
    }

    // When light mint account exists (decompressed), no validity proof needed - program reads from light mint account
    const merkleContext = mintInterface.merkleContext;
    const isDecompressed =
        mintInterface.mintContext?.cmintDecompressed ?? false;
    const validityProof = isDecompressed
        ? null
        : await rpc.getValidityProofV2(
              [
                  {
                      hash: bn(merkleContext.hash),
                      leafIndex: merkleContext.leafIndex,
                      treeInfo: merkleContext.treeInfo,
                      proveByIndex: merkleContext.proveByIndex,
                  },
              ],
              [],
              DerivationMode.compressible,
          );

    const ix = createUpdateMintAuthorityInstruction(
        mintInterface,
        currentMintAuthority.publicKey,
        newMintAuthority,
        payer.publicKey,
        validityProof,
    );

    const additionalSigners = currentMintAuthority.publicKey.equals(
        payer.publicKey,
    )
        ? []
        : [currentMintAuthority];

    const { blockhash } = await rpc.getLatestBlockhash();
    const tx = buildAndSignTx(
        [ComputeBudgetProgram.setComputeUnitLimit({ units: 500_000 }), ix],
        payer,
        blockhash,
        additionalSigners,
    );

    return await sendAndConfirmTx(rpc, tx, confirmOptions);
}

/**
 * Update the freeze authority of a light-token mint.
 * Works for both compressed and decompressed light mints.
 *
 * @param rpc                      RPC connection
 * @param payer                    Fee payer (signer)
 * @param mint                     Mint address
 * @param currentFreezeAuthority   Current freeze authority (signer)
 * @param newFreezeAuthority       New freeze authority (or null to revoke)
 * @param confirmOptions           Optional confirm options
 */
export async function updateFreezeAuthority(
    rpc: Rpc,
    payer: Signer,
    mint: PublicKey,
    currentFreezeAuthority: Signer,
    newFreezeAuthority: PublicKey | null,
    confirmOptions?: ConfirmOptions,
): Promise<TransactionSignature> {
    assertV2Enabled();

    const mintInterface = await getMintInterface(
        rpc,
        mint,
        confirmOptions?.commitment,
        LIGHT_TOKEN_PROGRAM_ID,
    );

    if (!mintInterface.merkleContext) {
        throw new Error(ERR_MINT_MISSING_MERKLE_CONTEXT);
    }

    // When light mint account exists (decompressed), no validity proof needed - program reads from light mint account
    const merkleContext = mintInterface.merkleContext;
    const isDecompressed =
        mintInterface.mintContext?.cmintDecompressed ?? false;
    const validityProof = isDecompressed
        ? null
        : await rpc.getValidityProofV2(
              [
                  {
                      hash: bn(merkleContext.hash),
                      leafIndex: merkleContext.leafIndex,
                      treeInfo: merkleContext.treeInfo,
                      proveByIndex: merkleContext.proveByIndex,
                  },
              ],
              [],
              DerivationMode.compressible,
          );

    const ix = createUpdateFreezeAuthorityInstruction(
        mintInterface,
        currentFreezeAuthority.publicKey,
        newFreezeAuthority,
        payer.publicKey,
        validityProof,
    );

    const additionalSigners = currentFreezeAuthority.publicKey.equals(
        payer.publicKey,
    )
        ? []
        : [currentFreezeAuthority];

    const { blockhash } = await rpc.getLatestBlockhash();
    const tx = buildAndSignTx(
        [ComputeBudgetProgram.setComputeUnitLimit({ units: 500_000 }), ix],
        payer,
        blockhash,
        additionalSigners,
    );

    return await sendAndConfirmTx(rpc, tx, confirmOptions);
}
